import {
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr
} from '@chakra-ui/react'
import React, { useMemo } from 'react'
import { type Column, useTable } from 'react-table'
import { usePatientControllerGetIntakeQuestionnaireResults } from '~/clinician-api'
import type { PatientIntakeQuestionnaireResultsDtoResponsesItem } from '~/clinician-api/models'
import { useIntakeControllerV1GetQuestionnaire } from '~/public-api'
import type {
  IntakeQuestionnaireDtoPagesItemItemItem,
  IntakeQuestionnaireDtoPagesItemItemItemOneOfFour
} from '~/public-api/models'

const isGroupItem = (
  item: IntakeQuestionnaireDtoPagesItemItemItem
): item is IntakeQuestionnaireDtoPagesItemItemItemOneOfFour => {
  return item.type === 'group'
}

interface QuestionWithAnswer {
  question: IntakeQuestionnaireDtoPagesItemItemItem
  answers: (PatientIntakeQuestionnaireResultsDtoResponsesItem | undefined)[]
}

export const IntakeQuestionnaireModal = ({
  patientId,
  isOpen,
  onClose
}: {
  patientId: string
  isOpen: boolean
  onClose: () => void
}) => {
  const { data: questionnaire } = useIntakeControllerV1GetQuestionnaire()
  const { data: result } =
    usePatientControllerGetIntakeQuestionnaireResults(patientId)

  const flattenedQuestionnaireItems = useMemo(() => {
    return questionnaire?.pages.flatMap(page => page.item)
  }, [questionnaire])

  const questionsWithAnswers = useMemo(() => {
    return flattenedQuestionnaireItems?.map(item => {
      if (isGroupItem(item)) {
        return {
          question: item,
          answers: item.item
            .sort((a, b) => (a.type === 'choice' ? -1 : 1))
            .map(q => {
              return result?.responses.find(r => r.linkId === q.linkId)
            })
            .filter(Boolean)
        }
      }
      return {
        question: item,
        answers: [result?.responses.find(r => r.linkId === item.linkId)].filter(
          Boolean
        )
      }
    })
  }, [flattenedQuestionnaireItems, result])

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="lg">Intake Questionnaire</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <IntakeQuestionnaireResultTable
            answers={questionsWithAnswers ?? []}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export const IntakeQuestionnaireResultTable = ({
  answers
}: {
  answers: QuestionWithAnswer[]
}) => {
  const columns: Column<QuestionWithAnswer>[] = useMemo(
    () => [
      {
        Header: 'Question',
        accessor: 'question',
        Cell: ({ row }) => {
          return (
            <Text fontWeight="bold">
              {row.index + 1}. {row.original.question.text}
            </Text>
          )
        },
        style: {
          width: 500
        },
        id: 'index'
      },
      {
        Header: 'Answer',
        accessor: 'answers',
        Cell: ({ value }) => {
          return (
            <Flex flexDirection="column" gap="small">
              {!value.length && <Text>--</Text>}
              {value.map(answer => (
                <Text key={answer?.linkId}>
                  {Array.isArray(answer?.value)
                    ? answer?.value.join(', ')
                    : answer?.value}
                </Text>
              ))}
            </Flex>
          )
        },
        style: {
          width: 400
        }
      }
    ],
    [answers]
  )

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({
      columns,
      data: answers,
      disableSortBy: true
    })

  return (
    <Table {...getTableProps()}>
      <Thead>
        {headerGroups.map((headerGroup, i) => (
          // @ts-expect-error
          <Tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column, j) => (
              <Th
                // @ts-expect-error
                key={column.id}
                {...column.getHeaderProps()}
                borderColor="light_gray"
                // @ts-expect-error
                sx={column.style}
              >
                <Flex alignItems="center">{column.render('Header')}</Flex>
              </Th>
            ))}
          </Tr>
        ))}
      </Thead>
      <Tbody {...getTableBodyProps()}>
        {rows.map((row, i) => {
          prepareRow(row)
          return (
            <Tr
              // @ts-expect-error
              key={row.id}
              {...row.getRowProps()}
              bg={i % 2 === 0 ? 'transparent' : 'rgba(45, 84, 232, 0.04)'}
            >
              {row.cells.map((cell, j) => (
                <Td
                  // @ts-expect-error
                  key={cell.id}
                  {...cell.getCellProps()}
                  borderColor="light_gray"
                  verticalAlign="middle"
                  pt="small"
                  pb="small"
                >
                  {cell.render('Cell')}
                </Td>
              ))}
            </Tr>
          )
        })}
      </Tbody>
    </Table>
  )
}
